import { useState, useEffect } from "react";
import { invoke, BridgeError } from "../lib/bridge";
import type { FamilyInfo } from "../lib/onboardingTypes";

interface JoinStatusResult {
  status: string;
  familyInfo: FamilyInfo | null;
}

interface WaitingForApprovalProps {
  joinRequestId: number;
  familyName: string;
  deviceLink?: boolean;
  onApproved: (info: FamilyInfo) => void;
  onRejected: () => void;
}

export function WaitingForApproval({ joinRequestId, familyName, deviceLink = false, onApproved, onRejected }: WaitingForApprovalProps) {
  const [rejected, setRejected] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const result = await invoke<JoinStatusResult>("check_join_status", { joinRequestId });
        if (result.status === "approved" && result.familyInfo) {
          onApproved(result.familyInfo);
        } else if (result.status === "rejected") {
          setRejected(true);
        }
      } catch (e) {
        console.error("Join status check failed:", String(e));
        // Only surface errors that indicate a definite problem (coded bridge errors),
        // not transient network failures which will resolve on the next poll.
        if (e instanceof BridgeError) {
          setError(String(e));
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [joinRequestId, onApproved, onRejected]);

  if (rejected) {
    return (
      <main className="container" style={{ textAlign: "center", paddingTop: "60px" }}>
        <h1 className="family-name">{familyName}</h1>
        <p style={{ color: "var(--color-hint)", marginTop: "24px" }}>Your request to join was declined.</p>
        <button onClick={onRejected} className="btn-secondary btn-full" style={{ marginTop: "24px" }}>
          Back
        </button>
      </main>
    );
  }

  return (
    <main className="container" style={{ textAlign: "center", paddingTop: "60px" }}>
      <h1 className="family-name">{familyName}</h1>
      <p style={{ color: "var(--color-hint)", marginTop: "24px" }}>
        {deviceLink
          ? "Linking this device to your account..."
          : "Waiting for a family member to approve your request..."}
      </p>
      <div style={{ marginTop: "24px", fontSize: "24px" }}>...</div>
      {error && <p style={{ color: "var(--color-error)", marginTop: "16px", fontSize: "14px" }}>{error}</p>}
    </main>
  );
}
